import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import { getWebViewLedgerReportDataApiHandler } from "../../../../services/AdminIndex";
import { parseData, reorderAccountData } from "../../../../utils/ledgerReportCalc";
import { REPORT_PRINT_CSS, ReportHeader } from "./reportPrintStyle";

// Headless-render-only page for the Auto Report Mail worker: Puppeteer
// navigates here, waits for #report-print-ready, then calls page.pdf().
// Not linked from anywhere in the app UI — this is not the interactive
// Ledger Report screen (see finishEntry/reports/LedgerReport.js), but it
// reuses that page's exact JSON-parsing/ordering logic
// (utils/ledgerReportCalc.js) instead of rendering the SP's raw JSON
// columns, which is what this page used to do.
const money = (v) => (v === "" || v == null ? "" : Number(v).toFixed(2));

function LedgerBlock({ account, fromDate }) {
  const accountData = account.accountData || [];
  const hasOpeningRow = accountData.some((item) => item?.accountName === "*Opening Balance");

  const totalDebit = accountData
    .reduce((total, t) => (t.type === "DR" ? total + Number(t?.amount || 0) : total), 0)
    .toFixed(2);
  const totalCredit = accountData
    .reduce((total, t) => (t.type === "CR" && !t?.isOpening ? total + Number(t?.amount || 0) : total), 0)
    .toFixed(2);
  const closingBalance = Number(accountData[accountData.length - 1]?.balance || 0).toFixed(2);

  const addr = account.address?.[0];
  const addressLine1 = addr
    ? [addr.address1, addr.address2, addr.address3].filter(Boolean).join(", ")
    : "";
  const addressLine2 = addr
    ? [addr.areaName, addr.cityName, [addr.Statename, addr.pin].filter(Boolean).join(" - ")].filter(Boolean).join(", ")
    : "";

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="rp-info">
        <b>Ledger Of:</b> {account.accountName}
        {account.GroupName ? <> &nbsp;&nbsp;<b>Group:</b> {account.GroupName}</> : null}
      </div>
      {addressLine1 ? <div className="rp-info">{addressLine1}</div> : null}
      {addressLine2 ? <div className="rp-info">{addressLine2}</div> : null}
      {account.GST || account.PANNo ? (
        <div className="rp-info">
          {account.GST ? <>GST No.: {account.GST} &nbsp;&nbsp;</> : null}
          {account.PANNo ? <>PAN No.: {account.PANNo}</> : null}
        </div>
      ) : null}

      <table className="rp-table" style={{ marginTop: 8 }}>
        <thead>
          <tr>
            <th>Doc No.</th>
            <th>Date</th>
            <th>Cheque No.</th>
            <th>Particulars</th>
            <th className="rp-num">Debit</th>
            <th className="rp-num">Credit</th>
            <th className="rp-num">Balance</th>
          </tr>
        </thead>
        <tbody>
          {!hasOpeningRow && (
            <tr>
              <td></td>
              <td className="rp-nowrap">{moment(fromDate).format("DD-MM-YYYY")}</td>
              <td></td>
              <td>*Opening Balance</td>
              <td></td>
              <td></td>
              <td className="rp-num">0.00</td>
            </tr>
          )}
          {accountData.map((acEntry, idx) => (
            <React.Fragment key={idx}>
              <tr>
                <td className="rp-nowrap">{acEntry?.transNo}</td>
                <td className="rp-nowrap">{moment(acEntry?.createdAt).format("DD-MM-YYYY")}</td>
                <td>{acEntry?.cheque || ""}</td>
                <td>
                  {acEntry?.accountName}
                  {acEntry?.narration1 ? <div style={{ color: "#666" }}>{acEntry.narration1}</div> : null}
                </td>
                <td className="rp-num">{!acEntry?.isOpening && acEntry?.type === "DR" ? money(acEntry?.amount) : ""}</td>
                <td className="rp-num">{!acEntry?.isOpening && acEntry?.type === "CR" ? money(acEntry?.amount) : ""}</td>
                <td className="rp-num">{money(acEntry?.balance)}</td>
              </tr>
            </React.Fragment>
          ))}
          <tr className="rp-total">
            <td colSpan={4}>Total</td>
            <td className="rp-num">{totalDebit}</td>
            <td className="rp-num">{totalCredit}</td>
            <td className="rp-num">{closingBalance}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function PrintLedgerReport() {
  const { companyId, accountId, fromDate, toDate } = useParams();
  const [searchParams] = useSearchParams();
  const company = searchParams.get("company") || "";
  const [accounts, setAccounts] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    getWebViewLedgerReportDataApiHandler({
      companyId,
      type: "detailReport",
      fromDate,
      toDate,
      accounts: JSON.stringify([Number(accountId)]),
      groups: JSON.stringify([]),
    })
      .then((res) => {
        const filtered = (res?.data || []).filter((ele) => ele?.accountData);
        const formatted = filtered.map((ele) => ({
          ...ele,
          address: parseData(ele?.address),
          accountData: reorderAccountData(parseData(ele?.accountData)),
        }));
        setAccounts(formatted);
      })
      .finally(() => setReady(true));
  }, [companyId, accountId, fromDate, toDate]);

  return (
    <div className="rp-page">
      <style>{REPORT_PRINT_CSS}</style>
      <ReportHeader
        company={company}
        title="Ledger Report"
        sub={`${moment(fromDate).format("DD-MM-YYYY")} to ${moment(toDate).format("DD-MM-YYYY")}`}
      />
      {accounts.length ? (
        accounts.map((account, idx) => (
          <LedgerBlock key={idx} account={account} fromDate={fromDate} />
        ))
      ) : (
        <div>No ledger transactions found for this period.</div>
      )}
      {ready ? <div id="report-print-ready" /> : null}
    </div>
  );
}
